import { useState, useCallback, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import NavigationDropdown from "../../components/NavigationDropdown";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import DownloadModalTables from "../../components/DownloadModalTables";
import CourtStatusFilter from "../../components/CourtStatusFilter";
import NavigationPanelLawyer from "../../components/NavigationPanelLawyer";
import Footer from "../../components/Footer";
import CTAs from "../../components/CTAs";
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { MoreVertical } from "lucide-react";
import { getCurrentUser } from "../../utils/getCurrentUser";
import { debtorDetailsService } from "../../services/debtorDetailsService";
import { Calendar, Danger, SearchNormal1, TickCircle } from "iconsax-reactjs";

const Dashboard = () => {
  const [isNavigationDropdownOpen, setNavigationDropdownOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isDownloadModalTablesOpen, setDownloadModalTablesOpen] =
    useState(false);
  const [isCourtStatusFilterOpen, setCourtStatusFilterOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState([]);
  const [caseFilter, setCaseFilter] = useState("all");
  const [timePeriod, setTimePeriod] = useState("12 months");

  const openNavigationDropdown = useCallback(() => {
    setNavigationDropdownOpen(true);
  }, []);

  const closeNavigationDropdown = useCallback(() => {
    setNavigationDropdownOpen(false);
  }, []);

  const closeLogoutConfirmation = useCallback(() => {
    setLogoutConfirmationOpen(false);
  }, []);

  const openDownloadModalTables = useCallback(() => {
    setDownloadModalTablesOpen(true);
  }, []);

  const closeDownloadModalTables = useCallback(() => {
    setDownloadModalTablesOpen(false);
  }, []);

  const openCourtStatusFilter = useCallback(() => {
    setCourtStatusFilterOpen(true);
  }, []);

  const closeCourtStatusFilter = useCallback(() => {
    setCourtStatusFilterOpen(false);
  }, []);

  const navItems = [
    {
      text: "Dashboard",
      bgColor: "bg-primary-primary-50",
      textColor: "text-neutrals-neutral-900",
      hoverBgColor: "bg-primary-primary-50",
      hoverTextColor: "text-neutrals-neutral-900",
      path: "",
    },
    {
      text: "Legal proceedings & billing",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/legal_1",
    },
    {
      text: "Pre-LBA checks",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/prelbacheck",
    },
    {
      text: "Client-debtor interaction",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/cdi1",
    },
  ];

  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const user = getCurrentUser();
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await debtorDetailsService.getDebtorDetails(
          user.email
        );
        console.log(",responseresponse",response);

        const mapped = response.map((item) => ({
          client_name: item.formData?.name || "N/A",
          date_issued: new Date(item.firm?.createdAt).toLocaleDateString(
            "en-GB"
          ),
          due_date: new Date(item.formData?.dueDate).toLocaleDateString(
            "en-GB"
          ),
          status: item.formData?.status || "Pending",
          lbaId:item.formData?._id,
          email:item.formData.email,
          amount:item.formData.amount,
          case_details: `${(
            item.firm?.formType || "Unknown"
          ).toUpperCase()} - ${item.formData?.invoiceNumber || "No Email"}`,
          action: "Contact law firm", // or any logic
        }));

        setInvoiceData(mapped);
      } catch (err) {
        console.error("Failed to fetch debtor details:", err);
      }
    };

    fetchData();
  }, [user.email]);
  // State for pagination
const [currentPage, setCurrentPage] = useState(1);
const [casesPerPage] = useState(10); // You can adjust this number

// Calculate pagination values
 const filterDataByTimePeriod = (data) => {
    const now = new Date();
    
    switch (timePeriod) {
      case "24 hours":
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        return data.filter(item => {
        
          const itemDate = new Date(item.due_date.split('/').reverse().join('-'));
          return itemDate >= oneDayAgo;
        });
        
      case "7 days":
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return data.filter(item => {
          const itemDate = new Date(item.due_date.split('/').reverse().join('-'));
          return itemDate >= sevenDaysAgo;
        });
        
      case "30 days":
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return data.filter(item => {
          const itemDate = new Date(item.due_date.split('/').reverse().join('-'));
          return itemDate >= thirtyDaysAgo;
        });
        
      case "12 months":
      default:
        return data; // Return all data for 12 months
    }
  };

  // Apply time period filter to your data
  const filteredByTimePeriod = filterDataByTimePeriod(invoiceData);
  
  // Then use filteredByTimePeriod in your existing filteredCases logic
  const filteredCases = filteredByTimePeriod.filter(caseItem => {
    const statusMap = {
      pending: "in progress",
      identified: "open", 
      resolved: "closed"
    };

    const normalizedStatus = statusMap[caseItem.status?.toLowerCase()] || caseItem.status?.toLowerCase();
    
    // Status filter
    const statusMatch = caseFilter === "all" || 
      normalizedStatus === caseFilter.toLowerCase();
    
    // Search filter
    const searchMatch = searchQuery === "" ||
      caseItem.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      caseItem.case_details.toLowerCase().includes(searchQuery.toLowerCase()) ||
      caseItem.status.toLowerCase().includes(searchQuery.toLowerCase());

    return statusMatch && searchMatch;
  });



  const filteredDebtors =
    activeTab === "all"
      ? invoiceData
      : invoiceData.filter(
          (debtor) => debtor.status.toLowerCase() === activeTab.toLowerCase()
        );

 
  const totalActiveCases = invoiceData.filter(
    (debtor) => debtor.status.toLowerCase() !== "Resolved"
  ).length;

  const pendingTasks = invoiceData.filter(
    (debtor) => debtor.status.toLowerCase() === "pending"
  ).length;
  const settledCases = invoiceData.filter(
    (debtor) => debtor.status.toLowerCase() === "resolved"
  ).length;
  const searchedDebtors = filteredDebtors.filter(
    (debtor) =>
      debtor.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      debtor.date_issued.toLowerCase().includes(searchQuery.toLowerCase()) ||
      debtor.due_date.toLowerCase().includes(searchQuery.toLowerCase()) ||
      debtor.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
      debtor.case_details.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const indexOfLastCase = currentPage * casesPerPage;
const indexOfFirstCase = indexOfLastCase - casesPerPage;
const currentCases = filteredCases.slice(indexOfFirstCase, indexOfLastCase);
const totalPages = Math.ceil(filteredCases.length / casesPerPage);

// Pagination handlers
const handleNextPage = () => {
  if (currentPage < totalPages) {
    setCurrentPage(currentPage + 1);
  }
};

const handlePreviousPage = () => {
  if (currentPage > 1) {
    setCurrentPage(currentPage - 1);
  }
};

// Reset to first page when filters change (add this useEffect)
useEffect(() => {
  setCurrentPage(1);
}, [filteredCases]);

  const caseStatusMap = {
    "In progress": "bg-white text-black border-gray-100",
    Open: "bg-white text-black border-gray-100",
    Closed: "bg-white text-black border-gray-100",
  };

  const caseStatusIcon = {
    Closed: <span style={{ color: "red" }}>•</span>,
    Open: <span style={{ color: "blue" }}>•</span>,
    "In progress": <span style={{ color: "green" }}>•</span>,
  };

  // For Cases overview pie chart
  const BG_COLOR = "#EDF2F4"; // Background color for empty space
  const COLORS_2 = ["#487C94", "#335869", "#EDF2F4"]; // Colors for true positive and false positive

  const [data1, setData1] = useState({
    Closed: 15,
    Open: 10,
    In_progress: 20,
  });
  const statusMapping = {
    pending: "In_progress",
    resolved: "Closed",
    identified: "Open",
  };

  useEffect(() => {
    const statusCounts = {
      Closed: 0,
      Open: 0,
      In_progress: 0,
    };

    invoiceData.forEach((debtor) => {
      const rawStatus = debtor.status?.toLowerCase();
      const mappedStatus = statusMapping[rawStatus];
      if (mappedStatus) {
        statusCounts[mappedStatus]++;
      }
    });

    setData1(statusCounts);
  }, [invoiceData]);

  // Convert object to array
  const pieData = Object.entries(data1).map(([key, value]) => ({
    name: key,
    value: value,
  }));

  const filteredInvoices =
    activeTab === "all"
      ? invoiceData
      : invoiceData.filter(
          (debtor) => debtor.status.toLowerCase() === activeTab.toLowerCase()
        );

  const searchedInvoices = filteredInvoices.filter(
    (debtor) =>
      debtor.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      debtor.date_issued.toLowerCase().includes(searchQuery.toLowerCase()) ||
      debtor.due_date.toLowerCase().includes(searchQuery.toLowerCase()) ||
      debtor.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
      debtor.action.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const invoiceStatusMap = {
    Pending:
      "bg-semantics-moderate-moderate-50 text-semantics-moderate-moderate-500 border-semantics-moderate-moderate-500",
    Identified:
      "bg-semantics-high-high-50 text-semantics-high-high-500 border-semantics-moderate-moderate-500",
    Resolved:
      "bg-semantics-low-low-50 text-semantics-low-low-500 border-semantics-low-low-500",
    Unpaid: "bg-orange-50 text-orange-500 border-orange-500",
  };

  const invoiceStatusIcon = {
    Pending: "•",
    Identified: "•",
    Resolved: "•",
    Unpaid: "•",
  };

  // For action column
  const ActionMenu = ({ debtor }) => {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    console.log("Debtor",debtor);

  const handleView = () => {
    navigate("/postprelbadet", {
      state: {
        lbaId: debtor?.lbaId,
        name: debtor?.client_name,
        currency: debtor?.currency,
        amount: debtor?.amount,
        address: debtor?.address,
        userId: getCurrentUser()?.id,
        email: debtor?.email,
        lawyer: true,
      },
      replace: true,
    });
  };

    return (
      <div className="relative">
        {/* three-dot icon */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-1 rounded-full hover:bg-white bg-white"
        >
          <MoreVertical className="w-5 h-5 text-neutrals-neutral-900" />
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="w-full absolute right-0 mt-2 bg-white border border-gray-200 shadow-lg rounded-lg z-10">
            <button
            className="w-full px-4 py-2 text-sm text-left bg-transparent text-neutrals-neutral-900 rounded hover:bg-[#e5e5e5] cursor-pointer"
            onClick={handleView}
          >
            View
          </button>
          {/*  <button className="w-full px-4 py-2 text-sm text-left bg-transparent text-neutrals-neutral-900 rounded hover:bg-[#e5e5e5] cursor-pointer">
              Download
            </button>*/}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="w-full relative bg-white min-h-screen overflow-visible text-left text-base text-neutrals-neutral-300 font-open-sans-font-family-meta">
        {/* Navigation Panel */}
        <NavigationPanelLawyer navItems={navItems} />

        {/* Main */}
        <div className="w-full absolute top-[136px] flex flex-col items-start justify-start gap-6 text-9xl text-neutrals-neutral-900 px-4 md:px-4 lg:px-24 box-border">
          <div className="self-stretch flex flex-col items-start justify-start gap-6">
            <div className="w-full flex flex-row items-start justify-start gap-4">
              <div className="flex-1 flex flex-col items-start justify-start gap-4">
                <b className="self-stretch relative">
                  Welcome back, {user.name}
                </b>
                <div className="self-stretch relative text-base text-neutrals-neutral-300">
                  Get to know time remaining for your LBA
                </div>
              </div>
              
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-0 text-sm">
              <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white flex flex-row items-center justify-start">
                    <button
                      onClick={() => setTimePeriod("12 months")}
                      className={`rounded-tl-3xs rounded-tr-none rounded-br-none rounded-bl-3xs border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-center py-2 px-4 ${
                        timePeriod === "12 months" 
                          ? "bg-column-bg font-semibold text-neutrals-neutral-900" 
                          : "bg-white text-neutrals-neutral-300"
                      }`}
                    >
                      <div className="relative font-semibold">12 months</div>
                    </button>
                    <button
                      onClick={() => setTimePeriod("30 days")}
                      className={`border-neutrals-neutral-100 border-solid border-t-[1px] border-r-[1px] border-b-[1px] flex flex-row items-center justify-center py-2 px-4 ${
                        timePeriod === "30 days" 
                          ? "bg-column-bg font-semibold text-neutrals-neutral-900" 
                          : "bg-white text-neutrals-neutral-300"
                      }`}
                    >
                      <div className="relative font-semibold">30 days</div>
                    </button>
                    <button
                      onClick={() => setTimePeriod("7 days")}
                      className={`border-neutrals-neutral-100 border-solid border-r-[1px] border-t-[1px] border-b-[1px] flex flex-row items-center justify-center py-2 px-4 ${
                        timePeriod === "7 days" 
                          ? "bg-column-bg font-semibold text-neutrals-neutral-900" 
                          : "bg-white text-neutrals-neutral-300"
                      }`}
                    >
                      <div className="relative font-semibold">7 days</div>
                    </button>
                    <button
                      onClick={() => setTimePeriod("24 hours")}
                      className={`rounded-tl-none rounded-tr-3xs rounded-br-3xs rounded-bl-none border-neutrals-neutral-100 border-solid border-t-[1px] border-r-[1px] border-b-[1px] flex flex-row items-center justify-center py-2 px-4 ${
                        timePeriod === "24 hours" 
                          ? "bg-column-bg font-semibold text-neutrals-neutral-900" 
                          : "bg-white text-neutrals-neutral-300"
                      }`}
                    >
                      <div className="flex flex-row items-center justify-center">
                        <div className="relative font-semibold">24 hours</div>
                      </div>
                    </button>
              </div>

            </div>
          </div>
          <div className="self-stretch flex flex-col items-start justify-start gap-6 text-base">
            <div className="self-stretch flex flex-row items-center justify-start gap-6 text-neutrals-neutral-300">
              <div className="w-full rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start justify-start">
                <div className="self-stretch flex flex-row items-start justify-between p-6 gap-0">
                  <div className="flex flex-col items-start justify-start gap-6">
                    <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-start justify-start p-2.5">
                      <TickCircle size={20} color="#2D404E" />
                    </div>
                    <div className="flex flex-col items-start justify-start gap-2.5">
                      <div className="relative">Total active cases</div>
                      <b className="relative text-13xl text-neutrals-neutral-900">
                        {totalActiveCases}
                      </b>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start justify-start">
                <div className="self-stretch flex flex-row items-start justify-between p-6 gap-0">
                  <div className="flex flex-col items-start justify-start gap-6">
                    <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-start justify-start p-2.5">
                      <Danger size={20} color="#2D404E" />
                    </div>
                    <div className="flex flex-col items-start justify-start gap-2.5">
                      <div className="relative">Pending tasks</div>
                      <b className="relative text-13xl text-neutrals-neutral-900">
                        {pendingTasks}
                      </b>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start justify-start">
                <div className="self-stretch flex flex-row items-start justify-between p-6 gap-0">
                  <div className="flex flex-col items-start justify-start gap-6">
                    <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-start justify-start p-2.5">
                      <Calendar size={20} color="#2D404E" />
                    </div>
                    <div className="flex flex-col items-start justify-start gap-2.5">
                      <div className="relative">Settled Cases</div>
                      <b className="relative text-13xl text-neutrals-neutral-900">
                        {settledCases}
                      </b>
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
            <div className="self-stretch flex flex-row items-start justify-start gap-6">
              {/* Pie chart */}
              <div className="self-stretch shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start justify-start p-6 gap-6">
                <div className="self-stretch border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-row items-center justify-between pt-0 px-0 pb-6 gap-0">
                  <div className="relative font-semibold">Cases overview</div>
                  <img
                    className="w-5 relative h-5 overflow-hidden shrink-0"
                    alt=""
                    src="assets/more-vert.svg"
                  />
                </div>
                <div className="self-stretch flex flex-row items-start justify-start gap-6 text-right text-sm text-neutrals-neutral-300">
                  <div className="w-60 h-60">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={110}
                          fill={BG_COLOR}
                          dataKey="value"
                          stroke="none"
                        >
                          {pieData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS_2[index % COLORS_2.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 h-[216px] flex flex-col items-start justify-between gap-0">
                    <div className="self-stretch flex flex-row items-start justify-start gap-1.5">
                      <div className="flex flex-row items-start justify-start pt-1.5 px-0 pb-0">
                        <div className="w-2 relative rounded-[50%] bg-primary-primary-500 h-2" />
                      </div>
                      <div className="flex flex-col items-start justify-start gap-1">
                        <div className="relative">Open</div>
                        <div className="relative text-5xl font-semibold text-neutrals-neutral-900 text-left">
                          {" "}
                          {data1.Open}
                        </div>
                      </div>
                    </div>
                    <div className="self-stretch flex flex-row items-start justify-start gap-1.5">
                      <div className="flex flex-row items-start justify-start pt-1.5 px-0 pb-0">
                        <div className="w-2 relative rounded-[50%] bg-primary-primary-300 h-2" />
                      </div>
                      <div className="flex flex-col items-start justify-start gap-1">
                        <div className="relative">In progress</div>
                        <div className="relative text-5xl font-semibold text-neutrals-neutral-900 text-left">
                          {" "}
                          {data1.In_progress}
                        </div>
                      </div>
                    </div>
                    <div className="self-stretch flex flex-row items-start justify-start gap-1.5">
                      <div className="flex flex-row items-start justify-start pt-1.5 px-0 pb-0">
                        <div className="w-2 relative rounded-[50%] bg-primary-primary-100 h-2" />
                      </div>
                      <div className="flex flex-col items-start justify-start gap-1">
                        <div className="relative">Closed</div>
                        <div className="relative text-5xl font-semibold text-neutrals-neutral-900 text-left">
                          {" "}
                          {data1.Closed}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="w-full h-[358px] flex-1 shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs border-neutrals-neutral-100 border-solid border-[1px] box-border overflow-hidden shrink-0 flex flex-col items-start justify-start text-lg">
                <div className="self-stretch rounded-t-3xs rounded-b-none flex flex-row items-center justify-between py-5 px-6 gap-0">
                  <div className="flex flex-row items-center justify-start gap-2.5">
                    <div className="relative font-semibold">
                      Recent invoices
                    </div>
                    <div className="rounded-8xs bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-start py-0.5 px-2 gap-1.5 text-sm">
                      <div className="w-1.5 relative rounded-[50%] bg-primary-primary-500 h-1.5" />
                      <div className="relative font-semibold">{totalActiveCases} entries</div>
                    </div>
                  </div>

                  <CTAs
                    type="Secondarybtn"
                    text="Filter"
                    borderColor="border-neutrals-neutral-100"
                    icon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M3 7h18M6 12h12M10 17h4"
                          stroke="#2d404e"
                          stroke-width="1.5"
                          stroke-linecap="round"
                        ></path>
                      </svg>
                    }
                  />
                </div>
                <div className="self-stretch flex flex-col items-start justify-start text-sm">
                  <div className="self-stretch flex flex-col items-start justify-start">
                    <div className="self-stretch flex flex-row items-start justify-start">
                      {/* Table */}
                      <table className="min-w-full divide-y divide-gray-200 border-collapse">
                        <thead className="bg-column-bg">
                          <tr>
                            <th
                              scope="col"
                              className="items-start justify-start py-3 px-4 border-t-[1px] border-b-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                            >
                              Client name
                            </th>
                            <th
                              scope="col"
                              className="items-start justify-start py-3 px-4 border-t-[1px] border-b-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                            >
                              Date issued
                            </th>
                            <th
                              scope="col"
                              className="items-start justify-start py-3 px-4 border-t-[1px] border-b-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                            >
                              Due date
                            </th>
                            <th
                              scope="col"
                              className="items-start justify-start py-3 px-4 border-t-[1px] border-b-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                            >
                              Status
                            </th>
                            <th
                              scope="col"
                              className="items-start justify-start py-3 px-4 border-t-[1px] border-b-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-neutrals-neutral-50 divide-y divide-gray-200">
                          {searchedInvoices.map((invoice) => (
                            <tr key={invoice.id} className="hover:bg-gray">
                              <td className="p-4 items-start border-solid border-b-[1px] border-neutrals-neutral-100">
                                {invoice.client_name}
                              </td>
                              <td className="p-4 items-start border-solid border-b-[1px] border-neutrals-neutral-100">
                                {invoice.date_issued}
                              </td>
                              <td className="p-4 items-start border-solid border-b-[1px] border-neutrals-neutral-100">
                                {invoice.due_date}
                              </td>
                              <td className="p-4 items-start border-solid border-b-[1px] border-neutrals-neutral-100 whitespace-nowrap">
                                <span
                                  className={`px-2 py-0.5 text-xs font-semibold rounded-full border-solid border-[1px] border-neutrals-neutral-100 ${
                                    invoiceStatusMap[invoice.status]
                                  }`}
                                >
                                  <span className="mr-1">
                                    {invoiceStatusIcon[invoice.status]}
                                  </span>
                                  {invoice.status}
                                </span>
                              </td>
                              <td className="p-4 items-start border-solid border-b-[1px] border-neutrals-neutral-100">
                                <ActionMenu debtor={invoice}/>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch flex flex-col items-start justify-start text-sm">
              <div className="w-full rounded-t-3xs rounded-b-none border-neutrals-neutral-100 border-solid border-t-[1px] border-r-[1px] border-l-[1px] box-border flex flex-row items-center justify-between py-5 px-6 gap-0 text-lg">
                <div className="flex flex-row items-center justify-start gap-2.5">
                  <div className="relative font-semibold">Active cases</div>
                  <div className="rounded-8xs bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-start py-0.5 px-2 gap-1.5 text-sm">
                    <div className="w-1.5 relative rounded-[50%] bg-primary-primary-500 h-1.5" />
                    <div className="relative font-semibold">{searchedDebtors.length} cases</div>
                  </div>
                </div>
                <CTAs
                  type="Secondarybtn"
                  text="Download"
                  borderColor="border-neutrals-neutral-100"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M9 11v6l2-2M9 17l-2-2"
                        stroke="#2d404e"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                      <path
                        d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5"
                        stroke="#2d404e"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                      <path
                        d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z"
                        stroke="#2d404e"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </svg>
                  }
                  onClick={openDownloadModalTables}
                />
              </div>
              <div className="w-full border-neutrals-neutral-100 border-solid border-t-[1px] border-r-[1px] border-l-[1px] box-border flex flex-row items-center justify-between py-3 px-6 gap-0">
                {/* Filter */}
              
                <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-start">
                  <button
                    onClick={() => setCaseFilter("all")}
                    className={`rounded-tl-3xs rounded-tr-none rounded-br-none rounded-bl-3xs border-neutrals-neutral-100 border-solid border-r-[1px] flex flex-row items-center justify-center py-2 px-4 ${
                      caseFilter === "all" 
                        ? "bg-column-bg font-semibold text-neutrals-neutral-900" 
                        : "bg-white text-neutrals-neutral-300"
                    }`}
                  >
                    <div className="relative font-semibold">View all</div>
                  </button>
                  <button
                    onClick={() => setCaseFilter("open")}
                    className={`border-neutrals-neutral-100 border-solid border-r-[1px] flex flex-row items-center justify-center py-2 px-4 ${
                      caseFilter === "open" 
                        ? "bg-column-bg font-semibold text-neutrals-neutral-900" 
                        : "bg-white text-neutrals-neutral-300"
                    }`}
                  >
                    <div className="relative font-semibold">Open</div>
                  </button>
                  <button
                    onClick={() => setCaseFilter("in progress")}
                    className={`border-neutrals-neutral-100 border-solid border-r-[1px] flex flex-row items-center justify-center py-2 px-4 ${
                      caseFilter === "in progress" 
                        ? "bg-column-bg font-semibold text-neutrals-neutral-900" 
                        : "bg-white text-neutrals-neutral-300"
                    }`}
                  >
                    <div className="relative font-semibold">In progress</div>
                  </button>
                  <button
                    onClick={() => setCaseFilter("closed")}
                    className={`rounded-tl-none rounded-tr-3xs rounded-br-3xs rounded-bl-none flex flex-row items-center justify-center py-2 px-4 ${
                      caseFilter === "closed" 
                        ? "bg-column-bg font-semibold text-neutrals-neutral-900" 
                        : "bg-white text-neutrals-neutral-300"
                    }`}
                  >
                    <div className="relative font-semibold">Closed</div>
                  </button>
                </div>
                <div className="flex flex-row items-start justify-start gap-2.5 text-neutrals-neutral-300">
                  {/* Replace the search div with input field */}
                  <div className="w-[294px] shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-row items-center justify-start py-2.5 px-3.5">
                    <div className="flex flex-row items-center justify-start gap-2.5">
                      <SearchNormal1 size={20} />
                      <input
                        type="text"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-transparent border-none outline-none w-full placeholder-neutrals-neutral-300"
                      />
                    </div>
                  </div>

                  <CTAs
                    type="Secondarybtn"
                    text="Filter"
                    borderColor="border-neutrals-neutral-100"
                    icon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M3 7h18M6 12h12M10 17h4"
                          stroke="#2d404e"
                          stroke-width="1.5"
                          stroke-linecap="round"
                        ></path>
                      </svg>
                    }
                    onClick={openCourtStatusFilter}
                  />
                </div>
              </div>
              <div className="w-full flex flex-col items-start justify-start">
                <div className="w-full flex flex-col items-start justify-start">
                  <div className="self-stretch flex flex-row items-start justify-start">
                    <table className="min-w-full divide-y divide-gray-200 border-collapse">
                      <thead className="bg-column-bg">
                        <tr>
                          <th
                            scope="col"
                            className="items-start justify-start py-3 px-4 border-t-[1px] border-b-[1px] border-l-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                          >
                            Case name
                          </th>
                          <th
                            scope="col"
                            className="items-start justify-start py-3 px-4 border-t-[1px] border-b-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                          >
                            Client name
                          </th>
                          <th
                            scope="col"
                            className="items-start justify-start py-3 px-4 border-t-[1px] border-r-[1px] border-b-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong"
                          >
                            Case status
                          </th>
                          {/* <th scope='col' className='items-start justify-start py-3 px-4 border-t-[1px] border-r-[1px] border-b-[1px] border-solid border-neutrals-neutral-100 font-open-sans-ff-meta-strong'>Next steps</th>*/}
                        </tr>
                      </thead>
                      {/* Replace the entire tbody section with: */}
                      <tbody className="bg-neutrals-neutral-50 divide-y divide-gray-200">
                        {filteredCases.map((case_list) => {
                          const statusMap = {
                            pending: "In progress",
                            identified: "Open",
                            resolved: "Closed",
                          };

                          const normalizedStatus = case_list.status?.toLowerCase();
                          const displayStatus = statusMap[normalizedStatus] || case_list.status;
                          const styleKey = displayStatus;

                          return (
                            <tr key={case_list.id} className="hover:bg-gray">
                              <td className="p-4 items-start border-solid border-b-[1px] border-l-[1px] border-neutrals-neutral-100">
                                {case_list.case_details}
                              </td>
                              <td className="p-4 items-start border-solid border-b-[1px] border-neutrals-neutral-100">
                                {case_list.client_name}
                              </td>
                              <td className="p-4 items-start border-solid border-b-[1px] border-r-[1px] border-neutrals-neutral-100">
                                <span
                                  className={`px-2 py-0.5 text-xs font-semibold rounded-md border-solid border-[1px] border-neutrals-neutral-100 ${caseStatusMap[styleKey]}`}
                                >
                                  <span className="mr-1">
                                    {caseStatusIcon[styleKey]}
                                  </span>
                                  {displayStatus}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Pagination */}
                <div className="w-full shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-t-none rounded-b-3xs border-neutrals-neutral-100 border-solid border-r-[1px] border-b-[1px] border-l-[1px] box-border flex flex-row items-center justify-between pt-3 px-6 pb-4 gap-0">
                    <CTAs
                      type="Secondarybtn"
                      text="Previous"
                      borderColor="border-neutrals-neutral-100"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                    />
                    <div className="relative">Page {currentPage} of {totalPages}</div>
                    <CTAs
                      type="Secondarybtn"
                      text="Next"
                      borderColor="border-neutrals-neutral-100"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                    />
                </div>
              </div>
              {/* Footer */}
              <Footer />
            </div>
          </div>
        </div>
      </div>
      {isNavigationDropdownOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeNavigationDropdown}
        >
          <NavigationDropdown onClose={closeNavigationDropdown} />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
      {isDownloadModalTablesOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeDownloadModalTables}
        >
          <DownloadModalTables onClose={closeDownloadModalTables}
          tableData={filteredCases}
            tag="lawyerdashboard" />
        </PortalPopup>
      )}
      {isCourtStatusFilterOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeCourtStatusFilter}
        >
          <CourtStatusFilter onClose={closeCourtStatusFilter} />
        </PortalPopup>
      )}
    </>
  );
};

export default Dashboard;
